import React, { useState, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

// UI Components
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

// Icons
import { Loader2, X } from "lucide-react";

// Shared & Assessment Components
import ImageUploader, {
  ImageDiagnostics,
} from "@/components/shared/ImageUploader";
import { PrescreenErrorOverlay } from "@/components/assessments/PrescreenErrorOverlay";
import { SoftPassBadge } from "@/components/assessments/SoftPassBadge";
import { PrescreenErrorState, SoftPassState } from "@/types/prescreen";

export interface UploadCardProps {
  imageUrl?: string | null;
  localPreviewUrl?: string | null;
  isProcessing: boolean;
  isPrescreening?: boolean;
  isLoading?: boolean;
  onUpload: (
    blob: Blob,
    fileName: string,
    diagnostics: ImageDiagnostics,
    previewUrl: string,
  ) => void;
  onClearImage?: () => void;
  lesionAreas?: { x: number; y: number; label: string }[];
  highlightLesions?: boolean;
  canClearImage?: boolean;
  prescreenError?: PrescreenErrorState;
  onRetry?: () => void;
  onReselect?: () => void;
  softPassState?: SoftPassState;
  extraContent?: React.ReactNode;
  processingStatusComponent?: React.ReactNode;
}

/** 窗口1：影像采集卡片 */
export const UploadCard: React.FC<UploadCardProps> = ({
  imageUrl,
  localPreviewUrl,
  isProcessing,
  isPrescreening = false,
  isLoading = false,
  onUpload,
  onClearImage,
  lesionAreas,
  highlightLesions,
  canClearImage,
  prescreenError,
  onRetry,
  onReselect,
  softPassState,
  extraContent,
  processingStatusComponent,
}) => {
  const { t } = useTranslation("assessments");

  // 图片尺寸状态
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const handleReady = useCallback(
    (payload: {
      previewUrl: string;
      uploadBlob: Blob;
      suggestedFileName: string;
      diagnostics: ImageDiagnostics;
    }) => {
      onUpload(
        payload.uploadBlob,
        payload.suggestedFileName,
        payload.diagnostics,
        payload.previewUrl,
      );
    },
    [onUpload],
  );

  // 处理删除图片按钮点击
  const handleClearImageClick = useCallback(() => {
    onClearImage?.();
    setImageDimensions(null);
  }, [onClearImage]);

  // 查看原图状态
  const [showFullImage, setShowFullImage] = useState(false);
  // 图片加载状态
  const [imageLoaded, setImageLoaded] = useState(false);
  // 骨架屏动画是否已完成（用于控制图片显示时序）
  const [skeletonExited, setSkeletonExited] = useState(false);

  const displayUrl = localPreviewUrl || imageUrl;
  // 原图 URL（优先使用 imageUrl，因为 localPreviewUrl 可能是压缩后的）
  const fullImageUrl = imageUrl || localPreviewUrl;

  // 是否正在从历史记录加载图片（同步计算，避免 useEffect 延迟导致闪动）
  // 条件：有远程图片 URL，但没有本地预览，且图片尚未加载完成
  const isLoadingFromHistory = !localPreviewUrl && !!imageUrl && !imageLoaded;

  // 是否显示骨架屏
  const showSkeleton = !imageLoaded;
  // 图片是否可以开始显示（图片已加载 且 骨架屏动画已退出）
  const canShowImage = imageLoaded && skeletonExited;

  // 加载图片获取尺寸
  useEffect(() => {
    if (displayUrl) {
      setImageLoaded(false);
      setSkeletonExited(false);
      const img = new Image();
      img.onload = () => {
        setImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
        setImageLoaded(true);
      };
      img.onerror = () => {
        // 加载失败时也标记为已加载，避免无限等待
        setImageLoaded(true);
      };
      img.src = displayUrl;
    } else {
      setImageDimensions(null);
      setImageLoaded(false);
      setSkeletonExited(false);
    }
  }, [displayUrl]);

  // 计算卡片尺寸：最短边为基准尺寸，长边按比例计算
  // 基准尺寸与 2、3 阶段卡片宽度一致：md:320px lg:360px
  const baseSize = { md: 320, xl: 360 };

  // 动态类名
  const cardClassName = useMemo(() => {
    if (!imageDimensions || !displayUrl) {
      // 无图片时使用固定正方形
      return "w-full aspect-square md:w-[320px] xl:w-[360px] md:aspect-auto md:h-[320px] xl:h-[360px]";
    }

    const { width, height } = imageDimensions;
    if (width >= height) {
      // 横图：高度固定，宽度自适应
      return "md:h-[320px] xl:h-[360px]";
    } else {
      // 竖图：宽度固定，高度自适应
      return "md:w-[320px] xl:w-[360px]";
    }
  }, [imageDimensions, displayUrl]);

  return (
    <>
      <motion.div
        layout
        transition={{
          layout: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
        }}
        className={cn(
          cardClassName,
          "flex-shrink-0 overflow-hidden p-0 gap-0 bg-white dark:bg-card shadow-[0_0_12px_rgba(0,0,0,0.08)] border border-white dark:border-border rounded-xl",
        )}
        style={
          imageDimensions && displayUrl
            ? {
                aspectRatio: `${imageDimensions.width} / ${imageDimensions.height}`,
              }
            : undefined
        }
      >
        <CardContent
          className={cn(
            "h-full transition-all duration-300",
            // 加载历史记录时减少 padding，让骨架屏更大
            (isLoading || isLoadingFromHistory) && !displayUrl
              ? "p-2 sm:p-3"
              : "p-4 sm:p-6",
          )}
        >
          {/* 图片区域 - 整个卡片用于显示图片，拍立得风格 */}
          <div className="relative w-full h-full bg-muted/30 overflow-hidden rounded-md">
            {displayUrl ? (
              <>
                {/* 图片加载骨架屏 - 带淡出动画 */}
                <AnimatePresence>
                  {!imageLoaded && (
                    <motion.div
                      key="image-skeleton"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="absolute inset-0 flex items-center justify-center z-10"
                    >
                      <Skeleton className="w-full h-full absolute inset-0" />
                      <Loader2 className="h-6 w-6 text-muted-foreground animate-spin relative z-10" />
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* 图片 - 骨架屏退出后再显示 */}
                <AnimatePresence onExitComplete={() => setSkeletonExited(true)}>
                  {showSkeleton && <motion.div key="skeleton-trigger" />}
                </AnimatePresence>
                <motion.img
                  src={displayUrl}
                  alt="分析图片"
                  className="w-full h-full object-cover cursor-zoom-in"
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{
                    opacity: canShowImage ? 1 : 0,
                    scale: canShowImage ? 1 : 1.02,
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  onClick={() => !isPrescreening && setShowFullImage(true)}
                />

                {/* 删除图片按钮 - 右上角 */}
                <AnimatePresence>
                  {canShowImage &&
                    canClearImage &&
                    !prescreenError?.visible && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2, delay: 0.15 }}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="secondary"
                              size="icon"
                              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950 border border-slate-200/60 dark:border-slate-700/60 shadow-none z-10"
                              onClick={handleClearImageClick}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            {t("pipeline.upload.clearImage")}
                          </TooltipContent>
                        </Tooltip>
                      </motion.div>
                    )}
                </AnimatePresence>
                {/* Soft Pass 角标 - 低置信度但放行时显示，与删除按钮同步消失 */}
                {canShowImage &&
                  canClearImage &&
                  softPassState?.visible &&
                  !prescreenError?.visible && (
                    <SoftPassBadge
                      visible={softPassState.visible}
                      tooltipMessage={softPassState.tooltipMessage}
                    />
                  )}
                {/* 预筛选错误内联反馈 */}
                {prescreenError?.visible &&
                  prescreenError.category &&
                  prescreenError.message && (
                    <PrescreenErrorOverlay
                      category={prescreenError.category}
                      message={prescreenError.message}
                      onRetry={onRetry || (() => {})}
                      onReselect={onReselect || (() => {})}
                      visible={prescreenError.visible}
                    />
                  )}
                {/* 扫描光效 - 优化版 */}
                {isProcessing && (
                  <>
                    {/* 外层光晕呼吸效果 */}
                    <motion.div
                      className="absolute -inset-1 rounded-xl pointer-events-none"
                      animate={{
                        opacity: [0.3, 0.6, 0.3],
                        scale: [1, 1.02, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.1) 50%, rgba(34, 197, 94, 0.15) 100%)",
                        filter: "blur(8px)",
                      }}
                    />
                    {/* 边框流光效果 */}
                    <motion.div
                      className="absolute inset-0 rounded-lg pointer-events-none overflow-hidden"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.4), transparent)",
                        backgroundSize: "200% 100%",
                      }}
                      animate={{
                        backgroundPosition: ["200% 0%", "-200% 0%"],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                    {/* 主扫描线 - 带拖尾效果 */}
                    <motion.div
                      className="absolute left-0 right-0 pointer-events-none"
                      style={{
                        height: "60px",
                        background:
                          "linear-gradient(180deg, transparent 0%, rgba(34, 197, 94, 0.05) 30%, rgba(34, 197, 94, 0.15) 50%, rgba(34, 197, 94, 0.05) 70%, transparent 100%)",
                      }}
                      initial={{ top: "-60px" }}
                      animate={{ top: "calc(100% + 60px)" }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: [0.4, 0, 0.6, 1],
                      }}
                    >
                      {/* 扫描线核心 */}
                      <div
                        className="absolute left-0 right-0 top-1/2 -translate-y-1/2"
                        style={{
                          height: "2px",
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(34, 197, 94, 0.5) 15%, rgba(34, 197, 94, 1) 50%, rgba(34, 197, 94, 0.5) 85%, transparent 100%)",
                          boxShadow:
                            "0 0 20px 4px rgba(34, 197, 94, 0.4), 0 0 40px 8px rgba(34, 197, 94, 0.2)",
                        }}
                      />
                    </motion.div>
                    {/* 网格扫描效果 */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none opacity-20"
                      style={{
                        backgroundImage: `
                        linear-gradient(rgba(34, 197, 94, 0.3) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(34, 197, 94, 0.3) 1px, transparent 1px)
                      `,
                        backgroundSize: "20px 20px",
                      }}
                      animate={{
                        opacity: [0.1, 0.25, 0.1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                    {/* 四角定位框 - 简洁的直角边框设计 */}
                    <div className="absolute inset-0 pointer-events-none">
                      {/* 左上角 */}
                      <motion.div
                        className="absolute top-3 left-3"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <div className="w-5 h-5 border-l-2 border-t-2 border-emerald-400 rounded-tl-sm" />
                      </motion.div>
                      {/* 右上角 */}
                      <motion.div
                        className="absolute top-3 right-3"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          delay: 0.2,
                        }}
                      >
                        <div className="w-5 h-5 border-r-2 border-t-2 border-emerald-400 rounded-tr-sm" />
                      </motion.div>
                      {/* 左下角 */}
                      <motion.div
                        className="absolute bottom-3 left-3"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          delay: 0.4,
                        }}
                      >
                        <div className="w-5 h-5 border-l-2 border-b-2 border-emerald-400 rounded-bl-sm" />
                      </motion.div>
                      {/* 右下角 */}
                      <motion.div
                        className="absolute bottom-3 right-3"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          delay: 0.6,
                        }}
                      >
                        <div className="w-5 h-5 border-r-2 border-b-2 border-emerald-400 rounded-br-sm" />
                      </motion.div>
                    </div>
                    {/* 中心十字准星 */}
                    <motion.div
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                      animate={{
                        opacity: [0.3, 0.7, 0.3],
                        scale: [0.95, 1.05, 0.95],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <div className="relative w-12 h-12">
                        {/* 水平线 */}
                        <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
                        {/* 垂直线 */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-emerald-400/60 to-transparent" />
                        {/* 中心点 */}
                        <motion.div
                          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-emerald-400/80"
                          animate={{
                            boxShadow: [
                              "0 0 4px 2px rgba(34, 197, 94, 0.3)",
                              "0 0 8px 4px rgba(34, 197, 94, 0.5)",
                              "0 0 4px 2px rgba(34, 197, 94, 0.3)",
                            ],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      </div>
                    </motion.div>
                  </>
                )}
                {/* 预检动画 - 仅边框呼吸灯效果 */}
                {isPrescreening && !isProcessing && (
                  <>
                    <motion.div
                      className="absolute inset-0 rounded-lg pointer-events-none"
                      animate={{
                        boxShadow: [
                          "inset 0 0 0 2px rgba(148, 163, 184, 0.2)",
                          "inset 0 0 0 2px rgba(148, 163, 184, 0.5)",
                          "inset 0 0 0 2px rgba(148, 163, 184, 0.2)",
                        ],
                      }}
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                    {/* 预筛选中央加载指示器 */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-black/40 backdrop-blur-sm rounded-full p-2">
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      </div>
                    </div>
                  </>
                )}
                {/* 病灶标注 */}
                {highlightLesions &&
                  lesionAreas?.map((area, idx) => (
                    <motion.div
                      key={idx}
                      className="absolute w-8 h-8 border-2 border-red-500 rounded-full"
                      style={{
                        left: `${area.x}%`,
                        top: `${area.y}%`,
                        transform: "translate(-50%, -50%)",
                      }}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: idx * 0.2 }}
                    >
                      <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs bg-red-500 text-white px-1.5 py-0.5 rounded whitespace-nowrap">
                        {area.label}
                      </span>
                    </motion.div>
                  ))}
                {/* 中央处理状态 - 显示动态文案组件 */}
                {isProcessing && processingStatusComponent && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {processingStatusComponent}
                    </motion.div>
                  </div>
                )}
              </>
            ) : isLoading || isLoadingFromHistory ? (
              // 加载历史记录时显示 skeleton - 填满整个卡片
              <div className="absolute inset-0 flex items-center justify-center bg-muted/30 rounded-md">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    {t("hints.loading.subtitle")}
                  </span>
                </div>
              </div>
            ) : (
              <ImageUploader
                className="w-full h-full"
                onReady={handleReady}
                targetSizeMB={0.8}
                targetWidthOrHeight={2048}
              >
                {extraContent}
              </ImageUploader>
            )}
          </div>
        </CardContent>
      </motion.div>

      {/* 原图查看 Dialog */}
      <Dialog open={showFullImage} onOpenChange={setShowFullImage}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden bg-black/95 border-none">
          <VisuallyHidden>
            <DialogTitle>
              {t("pipeline.upload.viewFullImage", "查看原图")}
            </DialogTitle>
          </VisuallyHidden>
          {fullImageUrl && (
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={fullImageUrl}
                alt="原图"
                className="max-w-full max-h-[85vh] object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
